import { Address } from '@mwe/models';
import { Adresse } from '../../../../generated/model/adresse.model';
import { Kunde } from '../../../../generated/model/kunde.model';
import { Unternehmen } from '../../../../generated/model/unternehmen.model';
import { Company } from '@shared-ikp/model/company.model';
import { MeterPoint } from '../../../model/meter-point.model';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { isUnternehmen } from './beteiligte-partei.util';
import { FALLBACK_VALUE } from './ummeldung.constants';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { mapDateStringToNgbDateStruct, mapNgbDateStructToDateString } from '@shared-ikp/utils/date.utils';
import {
  AdresseFormValue,
  BisherigerVertragspartnerFormValue,
  PrivatpersonKontaktdatenFormValue,
  RechnungsempfaengerFormValue,
  UnternehmenKontaktdatenFormValue,
  VertragspartnerMode,
} from '../schemas';
import { BeteiligtePartei } from '../../../../generated/model/beteiligte-partei.model';
import { Privatperson } from '../../../../generated/model/privatperson.model';

export function mapGeschaeftspartnerToUnternehmen(geschaeftspartner: Company): Unternehmen {
  return {
    name: geschaeftspartner.name,
    kundennummer: geschaeftspartner.gpNr,
    uid: geschaeftspartner.uid,
    adresse: mapAddressToAdresse(geschaeftspartner.address),
  };
}

export function mapRechnungsempfaengerToUnternehmen(zaehlpunkt: MeterPoint): Unternehmen {
  return {
    name: zaehlpunkt.rechnungsempfaengerName,
    kundennummer: zaehlpunkt.rechnungsempfaengerGpNr,
    uid: zaehlpunkt.rechnungsempfaengerUid,
    adresse: mapAddressToAdresse(zaehlpunkt.rechnungsAdresse),
  };
}

export function mapAddressToAdresse(address: Address | null | undefined): Adresse {
  return {
    ort: address?.city ?? '',
    plz: address?.postcode ?? '',
    strasse: address?.street ?? '',
    hausnummer: address?.streetNumber ?? '',
    stiege: address?.block ?? '',
    tuernummer: address?.doorNumber ?? '',
  };
}

export interface ContractPartnerReadonlyFields {
  name: string;
  kundennummer: string;
  adresse: string;
}

export interface InvoiceRecipientReadonlyFields extends ContractPartnerReadonlyFields {
  uid: string;
}

export function mapVertragspartnerToReadonlyFields(vertragspartner: Company | null): ContractPartnerReadonlyFields {
  return {
    name: vertragspartner?.name || FALLBACK_VALUE,
    kundennummer: vertragspartner?.gpNr || FALLBACK_VALUE,
    adresse: getAddressStreetAndPostCodeFormatted(vertragspartner?.address) || FALLBACK_VALUE,
  };
}

export function mapRechnungsempfaengerToReadonlyFields(zaehlpunkt: MeterPoint | null): InvoiceRecipientReadonlyFields {
  return {
    name: zaehlpunkt?.rechnungsempfaengerName || FALLBACK_VALUE,
    kundennummer: zaehlpunkt?.rechnungsempfaengerGpNr || FALLBACK_VALUE,
    adresse: getAddressStreetAndPostCodeFormatted(zaehlpunkt?.rechnungsAdresse) || FALLBACK_VALUE,
    uid: zaehlpunkt?.rechnungsempfaengerUid || FALLBACK_VALUE,
  };
}

export function mapBisherigerVertragspartnerToFormValue(bisherigerKunde: Kunde | null | undefined): BisherigerVertragspartnerFormValue {
  if (bisherigerKunde?.kundeTyp === Kunde.KundeTypEnum.UNBEKANNT) {
    return {
      vertragspartnerBekanntheit: 'unknown',
      vertragspartnerTyp: null,
      privatpersonKontaktdaten: null,
      unternehmenKontaktdaten: null,
      adresse: null,
    };
  }

  const form: BisherigerVertragspartnerFormValue = {
    vertragspartnerBekanntheit: 'known',
    vertragspartnerTyp: 'privat',
    privatpersonKontaktdaten: initalPrivatpersonKontaktdatenFormValue(),
    unternehmenKontaktdaten: initialUnternehmenKontaktdatenFormValue(),
    adresse: initalAdresseFormValue(),
  };

  form.adresse = mapAdresseToFormValue(bisherigerKunde?.rechnungsempfaenger?.adresse);

  if (isPrivatperson(bisherigerKunde?.vertragspartner)) {
    const privatperson = bisherigerKunde.vertragspartner;

    form.vertragspartnerTyp = 'privat';
    form.privatpersonKontaktdaten.vorname = privatperson.vorname ?? '';
    form.privatpersonKontaktdaten.nachname = privatperson.nachname ?? '';
    form.privatpersonKontaktdaten.email = bisherigerKunde.email ?? '';
    form.privatpersonKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
  } else if (bisherigerKunde?.vertragspartner) {
    const unternehmen = bisherigerKunde.vertragspartner;

    form.vertragspartnerTyp = 'unternehmen';
    form.unternehmenKontaktdaten.name = unternehmen.name ?? '';
    form.unternehmenKontaktdaten.geburtsdatum = mapDateStringToNgbDateStruct(unternehmen.geburtsdatum);
    form.unternehmenKontaktdaten.uid = unternehmen.uid ?? '';
    form.unternehmenKontaktdaten.firmenbuchnummer = unternehmen.firmenbuchnummer ?? '';
    form.unternehmenKontaktdaten.email = bisherigerKunde.email ?? '';
    form.unternehmenKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
  }

  return form;
}

function isPrivatperson(partei: BeteiligtePartei | null | undefined): partei is Privatperson {
  return partei !== undefined && partei !== null && ('vorname' in partei || 'nachname' in partei);
}

function mapAdresseToFormValue(adresse: Adresse | null | undefined): AdresseFormValue {
  return {
    plz: adresse?.plz ?? '',
    ort: adresse?.ort ?? '',
    strasse: adresse?.strasse ?? '',
    hausnummer: adresse?.hausnummer ?? '',
    stiege: adresse?.stiege ?? '',
    tuernummer: adresse?.tuernummer ?? '',
  };
}

export function initalPrivatpersonKontaktdatenFormValue(): PrivatpersonKontaktdatenFormValue {
  return {
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
  };
}

export function initialUnternehmenKontaktdatenFormValue(): UnternehmenKontaktdatenFormValue {
  return {
    name: '',
    geburtsdatum: null,
    uid: '',
    firmenbuchnummer: '',
    email: '',
    telefon: '',
  };
}

export function initalAdresseFormValue(): AdresseFormValue {
  return {
    plz: '',
    ort: '',
    strasse: '',
    hausnummer: '',
    stiege: '',
    tuernummer: '',
  };
}

export function mapRechnungsempfaengerToFormValue(rechnungsempfaenger: Unternehmen | null): RechnungsempfaengerFormValue {
  return {
    hasAbweichendenRechnungsempfaenger: rechnungsempfaenger !== null,
    name: rechnungsempfaenger?.name ?? '',
    adresse: {
      plz: rechnungsempfaenger?.adresse?.plz ?? '',
      ort: rechnungsempfaenger?.adresse?.ort ?? '',
      strasse: rechnungsempfaenger?.adresse?.strasse ?? '',
      hausnummer: getHausnummerStiege(rechnungsempfaenger?.adresse),
      stiege: '',
      tuernummer: rechnungsempfaenger?.adresse?.tuernummer ?? '',
    },
  };
}

export function mapRechnungsempfaengerFormValueToUnternehmen(value: RechnungsempfaengerFormValue): Unternehmen | null {
  if (!value.hasAbweichendenRechnungsempfaenger) {
    return null;
  }

  return {
    name: value.name.trim(),
    adresse: {
      plz: value.adresse.plz?.trim() ?? '',
      ort: value.adresse.ort?.trim() ?? '',
      strasse: value.adresse.strasse?.trim() ?? '',
      hausnummer: value.adresse.hausnummer?.trim() ?? '',
      stiege: value.adresse.stiege?.trim() ?? '',
      tuernummer: value.adresse.tuernummer?.trim() ?? '',
    },
  };
}

function getHausnummerStiege(address: Adresse | null | undefined): string {
  return [address?.hausnummer, address?.stiege].filter(Boolean).join('/');
}

export interface BisherigerKundeBuildParams {
  mode: VertragspartnerMode;
  zaehlpunkt: MeterPoint | null;
  vertragspartnerUnternehmen: Company | null;
  bisherigerKunde: Kunde | null | undefined;
  rechnungsempfaengerDraft?: RechnungsempfaengerFormValue;
}

/**
 * The single place where `bisherigerKunde` is assembled from the resolved mode, the loaded
 * Geschäftspartner company, the Zählpunkt and step 3's Rechnungsempfänger draft. Called by step 3
 * when it syncs to the store and by the store when the Geschäftspartner company finishes loading
 * (so a save before step 3 is ever visited still persists a complete `bisherigerKunde`); nothing
 * else derives `kundeTyp`/`vertragspartner`/`rechnungsempfaenger`.
 *
 * Without `rechnungsempfaengerDraft` (the store's eager call has no step-3 draft) the existing
 * `rechnungsempfaenger` is preserved. Fields owned by other interactions (`unterschrift`,
 * `hasZustimmung`) are preserved from the existing `bisherigerKunde` via spread.
 */
export function buildBisherigerKunde(bisherigerKundeBuildParams: BisherigerKundeBuildParams): Kunde {
  const { mode, zaehlpunkt, vertragspartnerUnternehmen, bisherigerKunde, rechnungsempfaengerDraft } = bisherigerKundeBuildParams;

  if (mode === VertragspartnerMode.BEARBEITBAR) {
    return { ...bisherigerKunde };
  }

  const vertragspartner = vertragspartnerUnternehmen
    ? mapGeschaeftspartnerToUnternehmen(vertragspartnerUnternehmen)
    : bisherigerKunde?.vertragspartner;

  if (mode === VertragspartnerMode.VERWALTETER_KUNDE) {
    return {
      ...bisherigerKunde,
      kundeTyp: Kunde.KundeTypEnum.VERWALTETER_KUNDE,
      vertragspartner,
      rechnungsempfaenger: zaehlpunkt ? mapRechnungsempfaengerToUnternehmen(zaehlpunkt) : (bisherigerKunde?.rechnungsempfaenger ?? null),
    };
  }

  return {
    ...bisherigerKunde,
    kundeTyp: Kunde.KundeTypEnum.ACCOUNT_KUNDE,
    vertragspartner,
    rechnungsempfaenger: rechnungsempfaengerDraft
      ? mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaengerDraft)
      : (bisherigerKunde?.rechnungsempfaenger ?? null),
  };
}

export function getStoredRechnungsempfaenger(ummeldung: Ummeldung | null): Unternehmen | null {
  const rechnungsempfaenger = ummeldung?.bisherigerKunde?.rechnungsempfaenger;
  return isUnternehmen(rechnungsempfaenger) ? rechnungsempfaenger : null;
}

export function mapBisherigerVertragspartnerFormValueToKunde(formValue: BisherigerVertragspartnerFormValue): Kunde {
  if (formValue.vertragspartnerBekanntheit === 'unknown') {
    return {
      kundeTyp: Kunde.KundeTypEnum.UNBEKANNT,
      vertragspartner: null,
      email: null,
      telefon: null,
    };
  }
  if (formValue.vertragspartnerTyp === 'privat') {
    return {
      vertragspartner: {
        vorname: formValue.privatpersonKontaktdaten.vorname.trim(),
        nachname: formValue.privatpersonKontaktdaten.nachname.trim(),
      },
      kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
      email: formValue.privatpersonKontaktdaten.email.trim(),
      telefon: formValue.privatpersonKontaktdaten.telefon.trim(),
      rechnungsempfaenger: {
        vorname: formValue.privatpersonKontaktdaten.vorname.trim(),
        nachname: formValue.privatpersonKontaktdaten.nachname.trim(),
        adresse: mapAdresseFormValueToAdresse(formValue.adresse),
      },
    };
  } else {
    return {
      kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
      vertragspartner: {
        name: formValue.unternehmenKontaktdaten.name.trim(),
        kundennummer: null,
        firmenbuchnummer: formValue.unternehmenKontaktdaten.firmenbuchnummer.trim(),
        uid: formValue.unternehmenKontaktdaten.uid.trim(),
        geburtsdatum: mapNgbDateStructToDateString(formValue.unternehmenKontaktdaten.geburtsdatum),
        adresse: mapAdresseFormValueToAdresse(formValue.adresse),
      },
      email: formValue.unternehmenKontaktdaten.email.trim(),
      telefon: formValue.unternehmenKontaktdaten.telefon.trim(),
      rechnungsempfaenger: {
        name: formValue.unternehmenKontaktdaten.name.trim(),
        firmenbuchnummer: formValue.unternehmenKontaktdaten.firmenbuchnummer.trim(),
        uid: formValue.unternehmenKontaktdaten.uid.trim(),
        adresse: mapAdresseFormValueToAdresse(formValue.adresse),
      },
    };
  }
}

export function mapAdresseFormValueToAdresse(formValue: AdresseFormValue): Adresse {
  return {
    plz: formValue.plz.trim(),
    ort: formValue.ort.trim(),
    strasse: formValue.strasse.trim(),
    hausnummer: formValue.hausnummer.trim(),
    stiege: formValue.stiege.trim(),
    tuernummer: formValue.tuernummer.trim(),
  };
}
